import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { existsFilter, pluralReadableTypeName } from '@iotv/core-services';
import { edgeTypeMatrix, EntityRollingProgramme, getGroupToEntityEdgeTypeId, getNiceDate, isEntityRollingProgrammeConstructorProps, NodeEdgeMapper, summarizeList, } from '@iotv/datamodel-core';
import { AdjacentType, ComparisonType, getZombieInstanceFromPrimaryKey, isNonNaNNumber, LogicalOperatorType, Milliseconds, } from '@iotv/iotv-v3-types';
import { Button, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { SimpleTreeView } from '@mui/x-tree-view';
import { useCallback, useContext, useEffect, useReducer, useRef } from 'react';
import { v4 } from 'uuid';
import { getSingleTypeRecursivePath } from '../../../../../../../../components/EnititySupport/RecusiveEntityGroup/getRecursiveQueryPath';
import { radioSelector, SelectAll } from '../../../../../../../../components/factories/DataGrid';
import { AppMainViewContext } from '../../../../../../../../ContextProviders/AppContextProvider';
import { NodeEdgeMapperContext } from '../../../../../../../../ContextProviders/NodeEdgeMapperComponentContext';
import { DeviceControlDAO } from '../../../../../../../../data/DeviceControlDAO';
import { isAdminRole } from '../../../../../../../../helpers/access';
import { timestampToISODateTime } from '../../../../../../../../helpers/format';
import { useModularTimePeriodSelector } from '../../../../../../../../hooks/useModularTimePeriodSelector';
import { useSelectedObjects } from '../../../../../../../../hooks/useSelectedObjects';
import { ActionIcons } from '../../../../../../../../Iconography';
import { ObjectViewType, Severity } from '../../../../../../../../types/AppTypes';
import { useColumnVisibilityForDataGrid } from '../../../../../ObjectsDataTable/hooks/useColumnVisibility';
import { ObjectTree } from '../../../../../ObjectTree';
import { AdminCardWrapper } from '../../../../common/AdminCardWrapper';
import { ItemTreeOnClickWrapper } from '../../../../common/ItemTreeOnClickWrapper';
import { ProgrammeHeader } from '../../../../common/ProgrammeHeader';
import { Timezone } from '../../../../common/Timezone';
import { EntityProgammeTimelineChart } from '../../../../EntityProgamme/Components/EntityTimelineChart';
import { TransactionIndicators } from '../../../../EntityProgamme/Components/TransactionStateIndicators';
import { getDataTable } from '../../../fns/getDataTable';
import { getScheduleContextInfo as entitySchedulesMatchesRequestedReported } from '../../../fns/scheduleContextInfo';
import { defaultEntityRollingProgrammeReducerState, EntityRollingProgrammeContext, } from '../../../Reducers/EntityRollingProgrammeReducer';
import { Actions } from './Actions';
import { getDataGridRowClassForWindowIndex, getStylesForColorCodes } from './colorCodes';
import { EnabledDisabledTooltip } from './EnabledDisabledTooltip';
import { RollingProgrammeWindowControls } from './RollingProgrammeControls';
import { ScheduleStatus } from './ScheduleStatus';
const debug = process.env.REACT_APP_DEBUG && false;
const TransationStateReducer = (prev, next) => {
    return { ...prev, ...next };
};
export const WindowAllocation = () => {
    'use no memo';
    // Error: Cannot access refs during render
    const { fromDateValue, toDateValue } = useModularTimePeriodSelector({
        rangeUnit: 'days',
        rangeLength: 1,
        allowedRangeUnits: ['days', 'hours'],
        behaviourFlags: { preventPeriodShiftIntoFuture: false },
        initialToTime: defaultEntityRollingProgrammeReducerState.toDateValue ?? undefined,
    });
    const [{ userSelectedGroup, contextCustomer, isSmallView, UserFunctions }] = useContext(AppMainViewContext);
    const [state, dispatch] = useContext(EntityRollingProgrammeContext);
    const { rollingProgramme, scheduler, schedulerHash, dataTableRows, windowData } = state;
    const [transactionState, dispatchTransactionState] = useReducer(TransationStateReducer, { loading: false, verifying: false, saving: false });
    const { state: { nemHash, nodeEdgeMapper, apexNode }, dispatch: nodeEdgeMapperDispatch, } = useContext(NodeEdgeMapperContext);
    const objectTreeNodeEdgeMapperRef = useRef(new NodeEdgeMapper());
    const selectableGroups = objectTreeNodeEdgeMapperRef.current.getNodesOfType('Group');
    const selectableItems = rollingProgramme ? objectTreeNodeEdgeMapperRef.current.getNodesOfType(rollingProgramme.supportedType) : [];
    const selectableGroupsAndItems = [...selectableGroups, ...selectableItems];
    const togglePresenceInSelectedObjectsShim = (v, id) => {
        toggleWindow(id);
    };
    const { state: [[selectedItems, setSelectedItems]], } = useSelectedObjects([], selectableGroupsAndItems);
    const { state: [[selectedWindows, setSelectedWindows]], handlers: { isInSelectedObjects: inSelectedWindows, addToSelectedObjects: addSelectedWindows, removeFromSelectedObjects: removeSelectedWindow, togglePresenceInSelectedObjects: toggleWindow, }, } = useSelectedObjects([], windowData ?? [], false);
    /** on change of selected windows */
    useEffect(() => {
        if (selectedWindows.length === 1) {
            const items = scheduler?.getItemsForWindow(selectedWindows[0].windowIdx);
            setSelectedItems(items ?? []);
        }
        else {
            setSelectedItems([]);
        }
    }, [selectedWindows]);
    const cardPadding = 16;
    const headerHeight = 48;
    const tabPadding = 8;
    const entities = scheduler?.getAllItems() ?? [];
    const getEntitySchedulesMatchesRequestedReported = useCallback(() => {
        if (nodeEdgeMapper && scheduler) {
            return entities.map((entity) => entitySchedulesMatchesRequestedReported({ entity, nodeEdgeMapper, scheduler, rollingProgramme }));
        }
        else {
            return [
                {
                    broadcastDataMatches: false,
                    reportedConfigMatches: false,
                    requestedConfigMatches: false,
                    enabledStateMatches: false,
                    persistedProgramedEnabled: false,
                    reportedProgammeEnabled: false,
                    requestedProgrammeEnabled: false,
                    requestIsInFlight: false,
                },
            ];
        }
    }, [nemHash, scheduler]);
    const currentMatchesRequestedReported = () => {
        if (nodeEdgeMapper && scheduler) {
            return getEntitySchedulesMatchesRequestedReported().every((m) => m.broadcastDataMatches === true && m.requestedConfigMatches === true && m.reportedConfigMatches === true);
        }
        else {
            return false;
        }
    };
    const reportedProgrammesAllDisabled = () => {
        if (nodeEdgeMapper && scheduler) {
            return getEntitySchedulesMatchesRequestedReported().every((m) => m.reportedProgammeEnabled === false);
        }
        else {
            return false;
        }
    };
    const getInFlightCount = () => {
        if (nodeEdgeMapper && scheduler) {
            return getEntitySchedulesMatchesRequestedReported().filter((m) => m.requestIsInFlight === false).length;
        }
        else {
            return 0;
        }
    };
    const currentMatchesLastBroadcast = () => {
        if (nodeEdgeMapper && scheduler) {
            return getEntitySchedulesMatchesRequestedReported().every((m) => m.broadcastDataMatches === true || m.reportedConfigMatches === true);
        }
        else {
            return false;
        }
    };
    const loadContextFromDatastore = async (scheduler) => {
        dispatchTransactionState({ loading: true });
        debug && console.log(`WindowAllocation used effect calling loadContextFromDatastore`);
        const { err, data: context } = await scheduler.loadContextFromDatastore();
        if (err) {
            console.error('loadContextFromDatastore', err);
        }
        if (context) {
            const action = { type: 'Recalculate', value: null };
            dispatch(action);
        }
        setTimeout(() => dispatchTransactionState({ loading: false }), 500);
    };
    /* on change of scheduler hash loadContextFromDatastore if not initialized*/
    useEffect(() => {
        if (scheduler) {
            if (scheduler.isInitialized) {
                debug && console.log(`WindowAllocation scheduler.isInitialized ${scheduler.isInitialized}`);
            }
            else {
                dispatchTransactionState({ loading: true });
                debug && console.log(`WindowAllocation used effect calling loadContextFromDatastore`);
                loadContextFromDatastore(scheduler);
            }
        }
    }, [schedulerHash]);
    /* on change of timeline period */
    useEffect(() => {
        debug && console.log(`WindowAllocation used effect on interval`, fromDateValue, toDateValue);
        const action = { type: 'SetDates', value: { fromDateValue, toDateValue } };
        dispatch(action);
    }, [fromDateValue, toDateValue]);
    const selectWindow = {
        valueGetter: (value, row, column, apiRef) => inSelectedWindows({ id: row.id }),
        renderCell: (gridCellParams) => radioSelector({ gridCellParams, valueSetter: togglePresenceInSelectedObjectsShim }),
        renderHeader: () => (_jsx(SelectAll, { items: windowData ?? [],
            selectedObjects: selectedWindows,
            setSelectedObjects: setSelectedWindows })),
    };
    const common = {
        sortable: false,
        resizable: false,
        filterable: false,
        disableColumnMenu: true,
    };
    const columns = [
        {
            field: 'id',
            ...common,
            ...selectWindow,
            width: 50,
            defaultVisible: true,
            hideSortIcons: true,
        },
        {
            field: 'startTimeUtcSeconds',
            headerName: 'Start time',
            ...common,
            width: 150,
            defaultVisible: true,
            valueGetter: (value) => `${timestampToISODateTime(value * Milliseconds.SECOND)}`,
        },
        {
            field: 'nextScheduledStartMs',
            headerName: 'Next start time',
            ...common,
            width: 150,
            defaultVisible: true,
            valueGetter: (value) => `${timestampToISODateTime(value)}`,
        },
        // { field: 'local_start_min', headerName: 'start min', defaultVisible: false, flex: 1 },
        // { field: 'displayTIme', headerName: 'Start', defaultVisible: true, flex: 1, minWidth: 75, valueGetter: (v, row,) => `${ printHrDay(row.local_start_hour, row.local_start_min) }` },
        {
            field: 'durationMins',
            headerName: 'Duration',
            ...common,
            width: 80,
            defaultVisible: true,
            valueGetter: (value) => `${value} mins`,
        },
        {
            field: 'delayMins',
            headerName: 'Repeats',
            ...common,
            width: 80,
            defaultVisible: true,
            valueGetter: (value) => `${value} mins`,
        },
        {
            field: 'entities',
            headerName: 'Scheduled',
            ...common,
            minWidth: 250,
            defaultVisible: true,
            valueGetter: (value) => summarizeList(value, 8),
        },
    ];
    const externalColumnVisibilityRef = useRef(Object.fromEntries(columns.map((c) => [c.field, c.defaultVisible])));
    const [columnVisibilityModel, setColumnVisibilityModel, onColumnVisibilityModelChange] = useColumnVisibilityForDataGrid({
        dataTableReference: `EntityProgramme`,
        objectTypeId: rollingProgramme?.supportedType ?? 'Generic',
        viewType: ObjectViewType.List,
        externalColumnVisibility: externalColumnVisibilityRef.current,
    });
    const startWindowRows = windowData ?? [];
    const handleStartTimeChange = (newValue) => {
        const wholeMinuteValue = newValue?.second(0).millisecond(0);
        debug && console.log('WindowAllocation.handleStartTimeChange got original fn val', newValue, wholeMinuteValue);
        const newTimeSecs = wholeMinuteValue ? wholeMinuteValue.unix() : undefined;
        if (newTimeSecs && isEntityRollingProgrammeConstructorProps(rollingProgramme)) {
            const action = {
                type: 'MutateRollingProgramme',
                value: {
                    update: { ...rollingProgramme, startTimeUtcSeconds: newTimeSecs },
                },
            };
            dispatch(action);
        }
    };
    const handleDurationChange = (e) => {
        const val = parseInt(e.target.value);
        if (isNonNaNNumber(val) && isEntityRollingProgrammeConstructorProps(rollingProgramme)) {
            const action = {
                type: 'MutateRollingProgramme',
                value: {
                    update: { ...rollingProgramme, durationMins: val },
                },
            };
            dispatch(action);
        }
    };
    const handleWindowGapDurationChange = (e) => {
        const val = parseInt(e.target.value);
        if (isNonNaNNumber(val) && isEntityRollingProgrammeConstructorProps(rollingProgramme)) {
            const action = {
                type: 'MutateRollingProgramme',
                value: {
                    update: { ...rollingProgramme, windowGapDurationMins: val },
                },
            };
            dispatch(action);
        }
    };
    const handleCycleGapDurationChange = (e) => {
        const val = parseInt(e.target.value);
        if (isNonNaNNumber(val) && isEntityRollingProgrammeConstructorProps(rollingProgramme)) {
            const action = {
                type: 'MutateRollingProgramme',
                value: {
                    update: { ...rollingProgramme, cycleGapDurationMins: val },
                },
            };
            dispatch(action);
        }
    };
    const handleWindowCountChange = (e) => {
        const val = parseInt(e.target.value);
        if (isNonNaNNumber(val) && isEntityRollingProgrammeConstructorProps(rollingProgramme)) {
            const action = {
                type: 'MutateRollingProgramme',
                value: {
                    update: { ...rollingProgramme, windows: val },
                },
            };
            dispatch(action);
        }
    };
    const changeState = scheduler?.getChangeState();
    const schedulerInitialized = scheduler !== null && scheduler.isInitialized;
    const changesArePresent = changeState !== undefined && (changeState.changed.length > 0 || changeState.new.length > 0 || changeState.deleted.length > 0);
    const canSave = schedulerInitialized && changesArePresent;
    const canLink = schedulerInitialized && changeState !== undefined && selectedItems.length > 0 && selectedWindows.length > 0;
    const canUnlink = schedulerInitialized &&
        changeState !== undefined &&
        selectedWindows.length > 0 &&
        (selectedItems.length > 0 || selectedWindows.map((w) => w.entities.length).reduce((s, c) => s + c) > 0);
    const canAddWindows = schedulerInitialized && selectedWindows.length === 1;
    const canSend = !canSave && !currentMatchesRequestedReported();
    const canStop = schedulerInitialized && scheduler?.lastPersitedState?.rollingProgramme.enabled === false && !reportedProgrammesAllDisabled();
    const handlePersist = async () => {
        let persistHandled = null;
        if (scheduler) {
            dispatchTransactionState({ saving: true });
            console.log('handlePersist change state', scheduler.getChangeState());
            const res = await scheduler?.persistChangeState();
            console.log('handlePersist res', res);
            dispatchTransactionState({ saving: false });
            const action = { type: 'Recalculate', value: null };
            dispatch(action);
            persistHandled = true;
            if (res.writeRes.err || !res.writeRes.data)
                UserFunctions.setUserMessage({
                    id: v4(),
                    type: 'MessageBar',
                    label: 'RollingProgrammed saved',
                    content: `Error saving ${rollingProgramme?.name}: ${res.writeRes.err?.message}`,
                    severity: Severity.error,
                });
            else
                UserFunctions.setUserMessage({
                    id: v4(),
                    type: 'SnackBar',
                    label: 'RollingProgrammed saved',
                    content: `Saved ${rollingProgramme?.name}`,
                    severity: Severity.success,
                });
        }
        return persistHandled;
    };
    const handleLinkSelectedItemsToWindows = async ({ items, windowProxy }) => {
        if (windowProxy) {
            const action = {
                type: 'LinkItemsToWindow',
                value: { items, windowProxy },
            };
            if (scheduler) {
                dispatchTransactionState({ verifying: true });
                dispatch(action);
                dispatchTransactionState({ verifying: false });
            }
            else {
                console.log(`WARN: handleLinkSelectedItemsToSlots no scheduler`);
            }
        }
    };
    const handleUnLinkSelectedItemsFromWindows = async ({ items }) => {
        const action = {
            type: 'UnlinkItemsFromWindow',
            value: { items },
        };
        if (scheduler) {
            dispatchTransactionState({ verifying: true });
            dispatch(action);
            dispatchTransactionState({ verifying: false });
        }
        else {
            console.log(`WARN: handleLinkSelectedItemsToSlots no scheduler`);
        }
    };
    const handleFlushWindows = async ({ windowProxies }) => {
        const action = {
            type: 'FlushWindows',
            value: { windowProxies },
        };
        if (scheduler) {
            dispatchTransactionState({ verifying: true });
            dispatch(action);
            dispatchTransactionState({ verifying: false });
        }
        else {
            console.log(`WARN: handleLinkSelectedItemsToSlots no scheduler`);
        }
    };
    const handleInsertWindowAbove = ({ windowProxy }) => {
        if (windowProxy && isNonNaNNumber(windowProxy.windowIdx)) {
            const action = {
                type: 'InsertWindowAbove',
                value: { windowProxy },
            };
            if (scheduler) {
                dispatchTransactionState({ verifying: true });
                dispatch(action);
                dispatchTransactionState({ verifying: false });
            }
            else {
                console.log(`WARN: handleLinkSelectedItemsToSlots no scheduler`);
            }
        }
    };
    const handleInsertWindowBelow = ({ windowProxy }) => {
        if (windowProxy && isNonNaNNumber(windowProxy.windowIdx)) {
            const action = {
                type: 'InsertWindowBelow',
                value: { windowProxy },
            };
            if (scheduler) {
                dispatchTransactionState({ verifying: true });
                dispatch(action);
                dispatchTransactionState({ verifying: false });
            }
            else {
                console.log(`WARN: handleLinkSelectedItemsToSlots no scheduler`);
            }
        }
    };
    const handleRemoveWindow = ({ windowProxy }) => {
        if (windowProxy && isNonNaNNumber(windowProxy.windowIdx)) {
            const action = {
                type: 'RemoveWindow',
                value: { windowProxy },
            };
            if (scheduler) {
                dispatchTransactionState({ verifying: true });
                dispatch(action);
                dispatchTransactionState({ verifying: false });
            }
            else {
                console.log(`WARN: handleLinkSelectedItemsToSlots no scheduler`);
            }
        }
    };
    /** this done entirely on aws side */
    const handleSendProgramme = async () => {
        let createHandled = null;
        if (rollingProgramme) {
            const { err, data } = await DeviceControlDAO.device.control.programme.rolling.send({ rollingProgramme });
            if (err || data) {
                if (err) {
                    console.log(`Err: createProgrammesRes, ${err.message}`);
                }
                if (data) {
                    console.log(`INFO: createProgrammesRes`, data);
                    const { broadcastData, rollingProgramme } = data;
                    if (isEntityRollingProgrammeConstructorProps(rollingProgramme)) {
                        /*
                        const action: RollingProgrammeReducerSetRollingProgrammeAction = {
                          type: "SetRollingProgramme",
                          value: { rollingProgramme: new EntityRollingProgramme(rollingProgramme) },
                        };
                        dispatch(action);
                        console.log(`INFO: disptached rolling programme action`, action);
                        */
                        /** TENTATIVE Completely restart context on save */
                        const apexNodeAction = { type: 'setApexNode', value: { apexNode: rollingProgramme } };
                        nodeEdgeMapperDispatch(apexNodeAction);
                        DeviceControlDAO.device.control.programme.rolling.getContext({ rollingProgramme }).then(({ err, data }) => {
                            if (data) {
                                const { nodeEdgeMapper, apexNode } = data;
                                const action = {
                                    type: 'loadNEMContents',
                                    value: { canaryString: 'WindowAllocation, handleCreateProgramme', nodeEdgeMapper },
                                };
                                nodeEdgeMapperDispatch(action);
                            }
                        });
                        const action = {
                            type: 'SetRollingProgramme',
                            value: { rollingProgramme: new EntityRollingProgramme(rollingProgramme) },
                        };
                        dispatch(action);
                        console.log(`INFO: disptached rolling programme action`, action);
                        createHandled = true;
                    }
                    else {
                        console.log(`WARN: did not send rolling programme action`, rollingProgramme);
                    }
                }
            }
            else {
                console.log(`WARN: createProgrammes returned neither err nor data`);
            }
        }
        return createHandled;
    };
    const handleEnableDisable = async (enabled) => {
        if (scheduler && rollingProgramme) {
            const action = {
                type: 'MutateRollingProgramme',
                value: {
                    update: { ...rollingProgramme, enabled },
                },
            };
            dispatch(action);
        }
    };
    const handleReset = async () => {
        if (scheduler) {
            const newScheduler = scheduler.getLastPersistedSchedulerState();
            if (newScheduler) {
                const action = { type: 'Reset', value: { scheduler: newScheduler } };
                dispatch(action);
            }
        }
    };
    const dataTable = dataTableRows ? getDataTable({ dataTableRows }) : null;
    const getRowClassName = useCallback((params) => {
        if (rollingProgramme && scheduler)
            return getDataGridRowClassForWindowIndex(params.row.windowIdx);
        return '';
    }, [rollingProgramme, scheduler]);
    const components = {
        header: (_jsxs(Stack, { direction: isSmallView ? 'column' : 'row', justifyContent: 'space-between', alignItems: 'center', children: [_jsx(EnabledDisabledTooltip, { rollingProgramme: rollingProgramme, scheduler: scheduler, children: _jsx(ProgrammeHeader, { programme: rollingProgramme, setEnabled: (enabled) => handleEnableDisable(enabled) }) }), _jsx(Timezone, {})] })),
        timeline: rollingProgramme && scheduler && scheduler.isInitialized ? (_jsx(EntityProgammeTimelineChart, { dataTable: dataTable, fragmentProps: { style: { height: 50 + 41, width: '100%' } }, timelineOptions: { timeline: { groupByRowLabel: true } } })) : (_jsx(Skeleton, { variant: "rounded", height: 41 + 'px', width: '100%', sx: { marginBlockEnd: '50px' } })),
        scheduleStatus: (_jsx(ScheduleStatus, { loaded: rollingProgramme !== null && scheduler !== null && scheduler.isInitialized, programme: rollingProgramme, status: getEntitySchedulesMatchesRequestedReported() })),
        windowControls: (_jsx(RollingProgrammeWindowControls, { rollingProgramme,
            handleCycleGapDurationChange,
            handleDurationChange,
            handleStartTimeChange,
            handleWindowCountChange,
            handleWindowGapDurationChange })),
        transactionState: _jsx(TransactionIndicators, { transactionState, loaded: rollingProgramme !== null }),
        rolling: rollingProgramme && scheduler ? (_jsx(DataGrid
        /**
         * ISSUE: when reducing the number of windows, and error like No row with id #8 found at `Object.getRowParams`
         * This is quite possibly addressed in [[DataGrid] Fix issue with Invalid Row Id #16631](https://github.com/mui/mui-x/pull/16631)
         */
        , { rows: startWindowRows,
            columns,
            columnVisibilityModel,
            onColumnVisibilityModelChange,
            autoPageSize: true,
            maxRows: 10,
            density: 'compact',
            disableRowSelectionOnClick: true,
            sortModel: [{ field: 'windowIdx', sort: 'asc' }],
            onRowClick: ({ id }) => {
                const window = scheduler.getAllWindows().find((window) => window.id === id);
                if (window) {
                    inSelectedWindows(window) ? removeSelectedWindow(window) : addSelectedWindows(window);
                }
            },
            getRowClassName,
            sx: Object.values(getStylesForColorCodes(rollingProgramme)) }, `${rollingProgramme.sk}_${startWindowRows.length}`)) : (_jsx(Skeleton, { variant: "rounded", height: '100%', width: '100%' })),
        groupsAndEntities: contextCustomer && rollingProgramme && rollingProgramme.supportedType ? (_jsxs(Stack, { height: '100%', children: [_jsx(Typography, { variant: "h6", children: isSmallView ? `Schedulable Items` : `Schedulable Groups and ${pluralReadableTypeName(rollingProgramme.supportedType)}` }), _jsx(SimpleTreeView, { sx: { height: '100%', overflowY: 'auto' }, children: _jsx(ObjectTree, { selectable: selectedWindows.length > 0,
                        setSelectedNodeIds: (sks) => setSelectedItems(sks
                            .map((sk) => objectTreeNodeEdgeMapperRef.current.getItem(getZombieInstanceFromPrimaryKey(sk)))
                            .filter(existsFilter)),
                        selectedNodeIds: [...selectedItems.map((item) => item.sk)],
                        nodeEdgeMapper: objectTreeNodeEdgeMapperRef.current,
                        hideTopStep: true,
                        contextObjects: [contextCustomer],
                        queryPaths: [
                            {
                                stepQuery: {
                                    objectTypeId: 'Group',
                                    edgeTypeId: edgeTypeMatrix.Customer.Group.customer_has_group.edgeTypeId,
                                    adjacencyType: AdjacentType.CHILD,
                                    nodeFilters: [
                                        {
                                            filters: [{ key: 'supportedType', predicate: ComparisonType.EQUALS, value: rollingProgramme.supportedType }],
                                            setOperation: LogicalOperatorType.AND,
                                        },
                                    ],
                                },
                                nextSteps: [
                                    {
                                        stepQuery: {
                                            objectTypeId: rollingProgramme.supportedType,
                                            edgeTypeId: getGroupToEntityEdgeTypeId(rollingProgramme.supportedType),
                                        },
                                    },
                                    ...getSingleTypeRecursivePath({
                                        groupObjectType: 'Group',
                                        containedObjectType: rollingProgramme.supportedType,
                                        ContainedTypeInstanceClickWrapper: ItemTreeOnClickWrapper,
                                    }),
                                ],
                            },
                        ] }) }, "groupsAndEntities")] })) : (_jsx(Skeleton, { variant: "rounded", height: '100%', width: '100%' })),
        buttons: (_jsx(Actions, { canAddAbove: canAddWindows,
            addAbove: () => handleInsertWindowAbove({ windowProxy: selectedWindows.at(0) }),
            canAddBelow: canAddWindows,
            addBelow: () => handleInsertWindowBelow({ windowProxy: selectedWindows.at(0) }),
            canLink,
            link: () => handleLinkSelectedItemsToWindows({ items: selectedItems, windowProxy: selectedWindows.at(0) }),
            canUnlink,
            unlink: () => handleFlushWindows({ windowProxies: selectedWindows }),
            canRemove: !canUnlink && canAddWindows,
            remove: () => handleRemoveWindow({ windowProxy: selectedWindows.at(0) }),
            canReset: canSave,
            reset: () => handleReset(),
            canSave,
            save: handlePersist,
            loaded: rollingProgramme !== null })),
        sendButton: (_jsx(Stack, { direction: 'row', justifyContent: 'center', children: _jsxs(Stack, { direction: isSmallView ? 'column' : 'row', alignItems: 'center', gap: 1, children: [_jsxs(Stack, { direction: 'row', alignItems: 'center', gap: 1, children: [_jsx(Typography, { fontStyle: 'italic', children: "Last sent:" }), _jsx(Typography, { fontStyle: 'italic', children: rollingProgramme?.appliedTimestampsMs && rollingProgramme.appliedTimestampsMs.length > 0
                                    ? getNiceDate(rollingProgramme.appliedTimestampsMs.at(0))
                                    : 'Never' })] }), _jsx(Button, { variant: "contained", size: "large", color: "success", disabled: !canSend || canStop, endIcon: _jsx(ActionIcons.Send, {}), onClick: () => handleSendProgramme(), children: "Send schedule" })] }) })),
        stopButton: (_jsx(Stack, { direction: 'row', justifyContent: 'center', children: _jsx(Tooltip, { title: 'To stop this programme: disable it and save it first', placement: "top", arrow: true, children: _jsx("span", { children: _jsx(Button, { variant: "contained", size: "large", color: "error", disabled: !canStop, startIcon: _jsx(ActionIcons.Stop, {}), onClick: () => handleSendProgramme(), children: "Stop schedule" }) }) }) })),
        toggleButton: (_jsx(Stack, { direction: 'row', justifyContent: 'center', children: _jsxs(Stack, { direction: isSmallView ? 'column' : 'row', alignItems: 'center', gap: 1, children: [_jsxs(Stack, { direction: 'row', alignItems: 'center', gap: 1, children: [_jsx(Typography, { fontStyle: 'italic', children: "Last sent:" }), _jsx(Typography, { fontStyle: 'italic', children: rollingProgramme?.appliedTimestampsMs && rollingProgramme.appliedTimestampsMs.length > 0
                                    ? getNiceDate(rollingProgramme.appliedTimestampsMs.at(0))
                                    : 'Never' })] }), !canStop ? (_jsx(Button, { variant: "contained", size: "large", color: "success", disabled: !canSend, endIcon: _jsx(ActionIcons.Send, {}), onClick: () => handleSendProgramme(), children: "Send schedule" })) : (_jsx(Button, { variant: "contained", size: "large", color: "error", startIcon: _jsx(ActionIcons.Stop, {}), onClick: () => handleSendProgramme(), children: "Stop schedule" }))] }) })),
        // leave for debug
        // changeState: (
        //   <Typography>{JSON.stringify({ new: changeState?.new, change: changeState?.changed, deleted: changeState?.deleted }, null, 1)}</Typography>
        // ),
    };
    return (_jsxs(Stack, { minHeight: isSmallView ? '200cqh' : isAdminRole(userSelectedGroup) ? `calc(100cqh - ${cardPadding * 2}px - ${headerHeight}px - ${tabPadding * 2}px)` : '100cqh', gap: 1, children: [_jsx(AdminCardWrapper, { children: _jsxs(Stack, { gap: 1, children: [components.header, components.scheduleStatus, components.timeline] }) }), _jsx(AdminCardWrapper, { cardProps: { sx: { flexGrow: 1, display: 'flex', flexDirection: 'row' } }, cardContentProps: { sx: { boxSizing: 'border-box', flexGrow: 1 } }, children: _jsxs(Stack, { height: '100%', flexGrow: 1, gap: 1, children: [components.transactionState, components.windowControls, _jsxs(Stack, { direction: isSmallView ? 'column' : 'row', flexGrow: 1, gap: 1, sx: { flexBasis: isSmallView ? '60.5em' : '30em', containerType: 'size' }, children: [_jsx("div", { style: { width: isSmallView ? '100cqw' : '50cqw', minHeight: '30em' }, children: components.rolling }), _jsx("div", { style: { width: isSmallView ? '100cqw' : '50cqw', minHeight: '30em' }, children: components.groupsAndEntities })] })] }) }), components.buttons, _jsx(Stack, { direction: isSmallView ? 'column' : 'row', justifyContent: 'center', gap: 1, children: components.toggleButton })] }));
};
